
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  IconButton,
  Badge,
  Progress,
  useColorModeValue,
  Heading,
  Card,
  CardBody,
  Select,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  useDisclosure,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverArrow,
  PopoverCloseButton,
  PopoverHeader,
  PopoverBody,
  Tag,
  TagLabel,
  Menu,
  useToast,
  Menu as ChakraMenu,
  MenuButton,
  MenuList,
  MenuItem
} from '@chakra-ui/react';
import React, { useState, useEffect } from 'react';
import { AddIcon, EditIcon, DeleteIcon, ViewIcon, RepeatIcon, ChevronDownIcon } from '@chakra-ui/icons';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Project } from '../types/index';

interface Assignment {
  id: number;
  name: string;
  role: 'Developer' | 'Tester' | 'Lead';
  joinDate: string;
  leaveDate?: string;
}

const statusOptions = [
  { value: 'planned', label: 'Planned' },
  { value: 'active', label: 'Active' },
  { value: 'on-hold', label: 'On Hold' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' },
];

const Projects: React.FC = () => {
  const navigate = useNavigate();
  const [viewingProject, setViewingProject] = useState<Project | null>(null);
  const { isOpen, onOpen, onClose } = useDisclosure();

  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');

  // Fetch projects and associates from backend
  const [projects, setProjects] = useState<Project[]>([]);
  const [associates, setAssociates] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const toast = useToast();
  // Handle status change
  const handleStatusChange = async (projectId: string, newStatus: string) => {
    try {
      const { projectsAPI } = await import('../services/api');
      const res = await projectsAPI.update(projectId, { status: newStatus as Project['status'] });
      setProjects((prev) => prev.map((p) =>
        p._id === projectId ? { ...p, status: newStatus as Project['status'] } : p
      ));
      toast({ status: 'success', title: 'Project status updated' });
    } catch (err: any) {
      toast({ status: 'error', title: 'Failed to update status', description: err.message });
    }
  };

  useEffect(() => {
    async function fetchData() {
      setLoading(true);
      setError(null);
      try {
        const [projectsRes, associatesRes] = await Promise.all([
          import('../services/api').then(m => m.projectsAPI.getAll()),
          import('../services/api').then(m => m.associateAPI.getAllAssociates()),
        ]);
        setProjects(projectsRes.data);
        setAssociates(associatesRes.data);
      } catch (err: any) {
        setError(err.message || 'Failed to fetch data');
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, []);

  // Helper to get associate name by ID
  const getAssociateName = (id: string) => {
    const found = associates.find(a => a._id === id);
    return found ? found.name : id;
  };

  const handleAddProject = () => {
    navigate('/projects/add');
  };

  const handleEditProject = (project: Project) => {
    navigate(`/projects/edit/${project._id}`);
  };

  const handleDeleteProject = (id: string) => {
    // Call API to delete project
    import('../services/api').then(m => m.projectsAPI.delete(id)).then(() => {
      setProjects(prev => prev.filter(p => p._id !== id));
    }).catch(err => {
      setError(err.message || 'Failed to delete project');
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'status.present';
      case 'completed':
        return 'status.pending';
      case 'on-hold':
        return 'status.late';
      case 'cancelled':
        return 'status.absent';
      default:
        return 'gray';
    }
  };

  const getProgressColor = (progress: number) => {
    if (progress >= 80) return 'green';
    if (progress >= 50) return 'blue';
    if (progress >= 20) return 'yellow';
    return 'red';
  };

  return (
    <Box>
      {loading && <Text>Loading projects...</Text>}
      <Box>
        {/* Page Header */}
        <Box mb={8}>
          <Heading size="lg" color="gray.800" mb={2}>
            Projects
          </Heading>
          <Text color="gray.600">
            Manage your client projects, timelines, and progress
          </Text>
        </Box>

        {/* Actions */}
        <Box mb={6}>
          <Button
            leftIcon={<AddIcon />}
            colorScheme="primary"
            onClick={handleAddProject}
            className="btn-primary"
          >
            Add Project
          </Button>
        </Box>

        {/* Projects Table - Responsive */}
        <Card bg={cardBg} border="1px" borderColor={borderColor}>
          <CardBody p={0}>
            <Box overflowX="auto">
              <Table variant="simple" minWidth="700px">
                <Thead>
                  <Tr>
                    <Th className="table-header" minWidth="70px" maxWidth="90px">Project</Th>
                    <Th className="table-header" minWidth="70px" maxWidth="90px">Client</Th>
                    <Th className="table-header" minWidth="100px" maxWidth="140px">Manager</Th>
                    <Th className="table-header" minWidth="70px" maxWidth="900px">Associates</Th>
                    <Th className="table-header" minWidth="60px" maxWidth="90px">Status</Th>
                    <Th className="table-header" minWidth="60px" maxWidth="80px">Actions</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {projects.map((project) => (
                    <Tr key={project._id}>
                      <Td className="table-cell" minWidth="80px" maxWidth="110px">
                        <VStack align="start" spacing={1}>
                          <Text fontWeight="medium" isTruncated>{project.name}</Text>
                          <Text fontSize="sm" color="gray.500" isTruncated>
                            ID:{project._id}
                          </Text>
                        </VStack>
                      </Td>
                      <Td className="table-cell" minWidth="70px" maxWidth="90px">
                        {typeof project.client === 'string' ? project.client : (project.client?.name || '-')}
                      </Td>
                      <Td className="table-cell" minWidth="140px" maxWidth="180px" pr={4}>
                        <VStack align="start" spacing={2}>
                          <Text fontSize="sm" fontWeight="medium">{project.managerName || '-'}</Text>
                          <HStack spacing={1}>
                            <Text fontSize="sm" color="gray.500" fontWeight="bold">Email:</Text>
                            <Menu>
                              <MenuButton as={Text} color="blue.500" textDecoration="underline" cursor="pointer" fontSize="sm">
                                {project.managerEmail || '-'}
                              </MenuButton>
                              <MenuList>
                                <MenuItem as="a" href={`mailto:${project.managerEmail}`}>Default Mail Client</MenuItem>
                                <MenuItem as="a" href={`https://mail.google.com/mail/?view=cm&fs=1&to=${project.managerEmail}`} target="_blank">Gmail</MenuItem>
                                <MenuItem as="a" href={`https://outlook.live.com/mail/0/deeplink/compose?to=${project.managerEmail}`} target="_blank">Outlook</MenuItem>
                              </MenuList>
                            </Menu>
                          </HStack>
                          <Text fontSize="sm" color="gray.500">{project.managerContact || '-'}</Text>
                        </VStack>
                      </Td>
                      <Td className="table-cell" minWidth="120px" maxWidth="160px" pl={4}>
                        {Array.isArray(project.associates) && project.associates.length > 0 ? (
                          <Popover trigger="hover" placement="right">
                            <PopoverTrigger>
                              <Tag colorScheme="purple" cursor="pointer">
                                <TagLabel>Team ({project.associates.length})</TagLabel>
                              </Tag>
                            </PopoverTrigger>
                            <PopoverContent>
                              <PopoverArrow />
                              <PopoverHeader>Team Members</PopoverHeader>
                              <PopoverBody>
                                <VStack align="start" spacing={1}>
                                  {project.associates.map((id: string) => (
                                    <Text key={id} fontSize="sm">{getAssociateName(id)}</Text>
                                  ))}
                                </VStack>
                              </PopoverBody>
                            </PopoverContent>
                          </Popover>
                        ) : (
                          <Text fontSize="sm" color="gray.400">No associates</Text>
                        )}
                      </Td>
                      <Td className="table-cell" minWidth="60px" maxWidth="90px">
                        <Badge
                          colorScheme={
                            project.status === 'in-progress' ? 'green' :
                            project.status === 'completed' ? 'blue' :
                            project.status === 'on-hold' ? 'yellow' :
                            project.status === 'active' ? 'green' :
                            project.status === 'planning' ? 'gray' :
                            project.status === 'cancelled' ? 'red' :
                            'gray'
                          }
                          variant="subtle"
                        >
                          {project.status ? project.status.toUpperCase() : '-'}
                        </Badge>
                      </Td>
                      <Td className="table-cell" minWidth="50px" maxWidth="70px">
                        <HStack spacing={2}>
                          <Menu>
                            <MenuButton
                              as={IconButton}
                              aria-label="Change status"
                              icon={<ChevronDownIcon />}
                              size="sm"
                              variant="outline"
                              title="Change status"
                            />
                            <MenuList minW="120px">
                              {['planned', 'active', 'on-hold', 'active', 'completed', 'cancelled', 'inactive'].map((status, idx) => (
                                <MenuItem
                                  key={idx}
                                  value={status}
                                  onClick={() => {
                                    if ((status === 'cancelled' || status === 'inactive') && Array.isArray(project.associates) && project.associates.length > 0) {
                                      toast({ status: 'warning', title: `Remove all associates to set status to ${status}` });
                                    } else {
                                      handleStatusChange(project._id, status);
                                    }
                                  }}
                                >
                                  {status.charAt(0).toUpperCase() + status.slice(1)}
                                </MenuItem>
                              ))}
                            </MenuList>
                          </Menu>
                          <IconButton
                            aria-label="View project"
                            icon={<ViewIcon />}
                            size="sm"
                            variant="ghost"
                            colorScheme="blue"
                            onClick={() => { setViewingProject(project); onOpen(); }}
                          />
                          <IconButton
                            aria-label="Edit project"
                            icon={<EditIcon />}
                            size="sm"
                            variant="ghost"
                            colorScheme="blue"
                            onClick={() => handleEditProject(project)}
                          />
                          <IconButton
                            aria-label="Delete project"
                            icon={<DeleteIcon />}
                            size="sm"
                            variant="ghost"
                            colorScheme="red"
                            onClick={() => handleDeleteProject(project._id)}
                          />
                        </HStack>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>
          </CardBody>
        </Card>
        {/* Modal for viewing project details */}
        <Modal isOpen={isOpen} onClose={onClose} size="lg">
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Project Details</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              {viewingProject && (
                <VStack align="start" spacing={3}>
                  <Text><b>Project Name:</b> {viewingProject.name}</Text>
                  <Text><b>Client:</b> {typeof viewingProject.client === 'string' ? viewingProject.client : (viewingProject.client?.name || '-')}</Text>
                  <Text><b>Status:</b> {viewingProject.status}</Text>
                  <Text><b>Manager Name:</b> {viewingProject.managerName || '-'}</Text>
                  <HStack spacing={1}>
                    <Text fontWeight="bold">Manager Email:</Text>
                    <Menu>
                      <MenuButton as={Text} color="blue.500" textDecoration="underline" cursor="pointer">
                        {viewingProject.managerEmail || '-'}
                      </MenuButton>
                      <MenuList>
                        <MenuItem as="a" href={`mailto:${viewingProject.managerEmail}`}>Default Mail Client</MenuItem>
                        <MenuItem as="a" href={`https://mail.google.com/mail/?view=cm&fs=1&to=${viewingProject.managerEmail}`} target="_blank">Gmail</MenuItem>
                        <MenuItem as="a" href={`https://outlook.live.com/mail/0/deeplink/compose?to=${viewingProject.managerEmail}`} target="_blank">Outlook</MenuItem>
                      </MenuList>
                    </Menu>
                  </HStack>
                  <Text><b>Manager Contact:</b> {viewingProject.managerContact || '-'}</Text>
                  <Text>
                    <b>Associates:</b> {Array.isArray(viewingProject?.associates) && viewingProject.associates.length > 0
                      ? viewingProject.associates.map((id: string) => getAssociateName(id)).join(', ')
                      : 'No associates'}
                  </Text>
                  <Text><b>Timeline:</b> {viewingProject.startDate} - {viewingProject.endDate}</Text>
                  <Text><b>Budget:</b> {viewingProject.currency} {viewingProject.budget.toLocaleString()}</Text>
                </VStack>
              )}
            </ModalBody>
          </ModalContent>
        </Modal>
      </Box>
    </Box>
  );
};

export default Projects;
